import {Component, computed, effect, inject, Signal, ViewChild} from '@angular/core';
import {Store} from '@ngrx/store';
import {SharedModalUtils} from '../../../../shared/shared-utils/shared-modal/shared-modal.utils';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';
import {_DEFAULT_APPLICATION_TOOLTIPS} from '../../../core/models';
import {IStoreOverrideSettings, SettingsReferenceEnum} from '../../models';
import {
  selectSelectedSetting, selectSelectedStoreAutoOrderOrderProcessedSettings,
  selectSelectedStoreAutoOrderOverrideSettings
} from '../../store/setting.selectors';
import {
  ICrudOptions,
  IUserAccessFeatureAutoOrderingSettings,
} from '../../../user-access/models/user-access-features/access';
import {
  selectUserAccess,
  selectUserAccessFeatureAutoOrderingSettingsForSelectedStore,
} from '../../../user-access/user-access-store/user-access.selectors';
import {IUserAccess} from '../../../user-access/models/user-access';
import {settingsSetSelected, settingStoreOverrides, settingStoreProcessedOrder} from '../../store/setting.actions';
import {
  FeatureAutoOrderOrderSettingsComponent,
} from '../feature-auto-order-order-settings/feature-auto-order-order-settings.component';
import {
  FeatureAutoOrderSchedulerSettingsComponent,
} from '../feature-auto-order-scheduler-settings/feature-auto-order-scheduler-settings.component';
import {ISharedModalBasic} from '../../../../shared/shared-models/modals/shared-modal-basic';
import {IModalDismissResult} from '../../../../shared/shared-services/overlay/modal-controller.service';
import {IStore} from '../../../../shared/shared-models/store/store';
import {selectSelectedStore} from '../../../user-access/user-stores-store/user-store.selectors';
import {IProcessedOrderSettings} from '../../models/auto-ordering/ao-processed-order-settings.interface';
import {isStoreAdminForStore} from '../../../../shared/shared-utils/user-access/user-access.utils';

@Component({
  selector: 'app-auto-order-general-settings',
  standalone: false,
  templateUrl: './feature-auto-order-general-settings.component.html',
  styleUrl: './feature-auto-order-general-settings.component.scss',
})
export class FeatureAutoOrderGeneralSettingsComponent {

  //#region INJECTORS
  private _store = inject(Store);
  private modalUtils = inject(SharedModalUtils);
  //#endregion

  //#region VIEW CHILDREN
  @ViewChild(FeatureAutoOrderOrderSettingsComponent) orderComp: FeatureAutoOrderOrderSettingsComponent;
  @ViewChild(FeatureAutoOrderSchedulerSettingsComponent) schedulerComp: FeatureAutoOrderSchedulerSettingsComponent;
  //#endregion

  //#region PROPERTIES
  orderBlankSupplierCode: boolean = false;
  priceEditsAfterProcessed: boolean = false;

  protected readonly icons = IconsEnum;
  protected readonly iconColours = IconColourEnum;
  protected readonly tooltips = _DEFAULT_APPLICATION_TOOLTIPS;

  private readonly _overrideSettings: Signal<IStoreOverrideSettings> = this._store.selectSignal(selectSelectedStoreAutoOrderOverrideSettings);
  private readonly _priceEditSettings: Signal<IProcessedOrderSettings> = this._store.selectSignal(selectSelectedStoreAutoOrderOrderProcessedSettings);
  private readonly _selectedStore: Signal<IStore> = this._store.selectSignal(selectSelectedStore);
  private readonly _settingChanged: Signal<SettingsReferenceEnum> = this._store.selectSignal(selectSelectedSetting);
  private readonly _uaOrderingSettings: Signal<IUserAccessFeatureAutoOrderingSettings> = this._store.selectSignal(selectUserAccessFeatureAutoOrderingSettingsForSelectedStore);
  private readonly _userAccess: Signal<IUserAccess> = this._store.selectSignal(selectUserAccess);
  protected readonly isAdmin = computed((): boolean =>
    isStoreAdminForStore(this._userAccess(), this._selectedStore()?.storeId),
  );
  #hasOverridesBeenEdited: boolean = false;
  #hasPriceEditsBeenEdited: boolean = false;
  //#endregion

  //#region INITIALIZATION
  constructor() {
    effect(() => {
      const override = this._overrideSettings();
      const priceEdits = this._priceEditSettings();
      const access = this._uaOrderingSettings();
      this.initializeForms(access, override, priceEdits);
    });
    effect(() => {
      const ref = this._settingChanged();
      this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
    });
  }

  //#endregion

  //#region ACCESSOR - OVERRIDES

  get hasOverridesBeenEdited(): boolean {
    return this.#hasOverridesBeenEdited;
  }

  get isOverridesSubmitDisabled(): boolean {
    return (this.canOverridesBeEdited === false) || (this.canOverridesBeEdited === true && this.hasOverridesBeenEdited === false);
  }

  get canOverridesBeEdited(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canEdit ?? false;
  }

  get canOverridesBeSubmitted(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canSubmit ?? false;
  }

  get canOverridesBeViewed(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - PRICE EDITS

  get hasPriceEditsBeenEdited(): boolean {
    return this.#hasPriceEditsBeenEdited;
  }

  get isPriceEditsSubmitDisabled(): boolean {
    return (this.canPriceEditsBeEdited === false) || (this.canPriceEditsBeEdited === true && this.hasPriceEditsBeenEdited === false);
  }

  get canPriceEditsBeEdited(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canEdit ?? false;
  }

  get canPriceEditsBeSubmitted(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canSubmit ?? false;
  }

  get canPriceEditsBeViewed(): boolean {
    const access = this.orderingSettingsUserAccess?.openOrderSettings;
    return access?.canView ?? false;
  }

  //#endregion

  //#region ACCESSOR - OVERRIDE SETTINGS + USER ACCESS + STORE
  get overrideSettings(): IStoreOverrideSettings {
    return this._overrideSettings();
  }

  get priceEditSettings(): IProcessedOrderSettings {
    return this._priceEditSettings();
  }

  get orderingSettingsUserAccess(): IUserAccessFeatureAutoOrderingSettings {
    return this._uaOrderingSettings();
  }

  //#endregion

  //#region ACCESSOR - ALL/GLOBAL
  get haveAnyEditsBeenMade(): boolean {
    return this.#hasOverridesBeenEdited
      || this.#hasPriceEditsBeenEdited
      || (this.orderComp?.haveAnyEditsBeenMade ?? false)
      || (this.schedulerComp?.haveAnyEditsBeenMade ?? false);
  }

  //#endregion

  //#region FORM - CLICK HANDLERS
  onRevertForm(form: string): void {
    switch (form) {
      case 'all':
        this.revertOverrides();
        this.revertPriceEdits();
        this.orderComp?.onRevertForm('all');
        this.schedulerComp?.onRevertForm('all');
        break;
      case 'priceEdits':
        this.revertPriceEdits();
        break;
      case 'overrides':
      default:
        this.revertOverrides();
        break;
    }
  }

  onSubmitForm(form: string): void {
    switch (form) {
      case 'all':
        if (this.canOverridesBeSubmitted) {
          this._store.dispatch(settingStoreOverrides.setStoreOverride({
            overrides: {
              ...this.overrideSettings,
              orderBlankSupplierCode: this.orderBlankSupplierCode,
            },
          }));
          this.#hasOverridesBeenEdited = false;
        }
        if (this.canPriceEditsBeSubmitted) {
          this._store.dispatch(settingStoreProcessedOrder.setStoreProcessedOrder({
            processedOrder: {
              ...this.priceEditSettings,
              priceEditsAfterProcessed: this.priceEditsAfterProcessed,
            },
          }));
          this.#hasPriceEditsBeenEdited = false;
        }
        this.orderComp?.onSubmitForm('all');
        this.schedulerComp?.onSubmitForm('all');
        break;
      case 'overrides':
        if (!this.canOverridesBeSubmitted) break;
        this._store.dispatch(settingStoreOverrides.setStoreOverride({
          overrides: {
            ...this.overrideSettings,
            orderBlankSupplierCode: this.orderBlankSupplierCode,
          },
        }));
        this.#hasOverridesBeenEdited = false;
        break;
      case 'priceEdits':
        if (!this.canPriceEditsBeSubmitted) break;
        this._store.dispatch(settingStoreProcessedOrder.setStoreProcessedOrder({
          processedOrder: {
            ...this.priceEditSettings,
            priceEditsAfterProcessed: this.priceEditsAfterProcessed,
          },
        }));
        this.#hasPriceEditsBeenEdited = false;
        break;
      default:
        break;
    }
  }

  onToggleSwitch(field: string, toggleValue: boolean): void {
    switch (field) {
      case 'orderBlankSupplierCode':
        if (this.orderBlankSupplierCode !== toggleValue) {
          this.orderBlankSupplierCode = toggleValue;
          this.#hasOverridesBeenEdited = true;
        }
        break;
      case 'priceEditsAfterProcessed':
        if (this.priceEditsAfterProcessed !== toggleValue) {
          this.priceEditsAfterProcessed = toggleValue;
          this.#hasPriceEditsBeenEdited = true;
        }
        break;
      default:
        break;
    }
  }

  //#endregion

  //#region FORM - BUILD FORMS FROM ACCESS

  private initializeForms(access: IUserAccessFeatureAutoOrderingSettings, override: IStoreOverrideSettings, priceEdits: IProcessedOrderSettings): void {
    this.buildOverridesForm(access?.openOrderSettings, override?.orderBlankSupplierCode);
    this.buildPriceEditsForm(access?.openOrderSettings, priceEdits?.priceEditsAfterProcessed);
  }

  private buildOverridesForm(access: ICrudOptions, orderBlankSupplierCode: boolean): void {
    if (access?.canView && this.hasOverridesBeenEdited === false) {
      this.orderBlankSupplierCode = orderBlankSupplierCode ?? false;
    }
  }

  private buildPriceEditsForm(access: ICrudOptions, priceEditsAfterProcessed: boolean): void {
    if (access?.canView && this.hasPriceEditsBeenEdited === false) {
      this.priceEditsAfterProcessed = priceEditsAfterProcessed ?? false;
    }
  }

  //#endregion

  //#region FORM - REVERT FORM EDITS
  revertOverrides(): void {
    this.orderBlankSupplierCode = this.overrideSettings?.orderBlankSupplierCode ?? false;
    this.#hasOverridesBeenEdited = false;
  }

  revertPriceEdits(): void {
    this.priceEditsAfterProcessed = this.priceEditSettings?.priceEditsAfterProcessed ?? false;
    this.#hasPriceEditsBeenEdited = false;
  }

  //#endregion

  //#region MODAL - DISCARD UNSAVED CHANGES
  private async onShowModalUnsavedChanges(ref: SettingsReferenceEnum): Promise<void> {
    const modalProps: ISharedModalBasic = {
      buttonAccept: true,
      buttonAcceptText: 'Discard Changes & Continue',
      buttonClose: true,
      buttonCloseCross: false,
      buttonCloseText: 'Cancel',
      modalTitle: 'Unsaved Changes',
      modalTitleIcon: IconsEnum.infoSlabCircleOutline,
      modalTitleIconColor: IconColourEnum.yellow,
      contextHeading: 'If you leave this page, your changes will be lost.',
      contextDescription: ['Are you sure you want to continue?'],
      cssClass: ['shared-basic-modal-css-small'],
    };
    await this.modalUtils.createModal(modalProps).then((result: IModalDismissResult) => {
      this._store.dispatch(settingsSetSelected.setSelectedReference({ref: SettingsReferenceEnum.featureAutoOrderGeneralSettings}));
      if (result.role === 'modal-accept') {
        this._store.dispatch(settingsSetSelected.setSelectedReferenceForNavigation({ref}));
      }
    });
  }

  //#endregion

}
